import { Injectable, signal } from '@angular/core';
import { Api } from '../api/api';
import { adminBlockchainsControllerListBlockchainsV1 } from '../api/functions';
import { BlockchainResponseAdminDto } from '../api/models/blockchain-response-admin-dto';

export interface SelectOption<T = any> {
  label: string;
  value: T;
}

@Injectable({ providedIn: 'root' })
export class BlockchainsLookupService {
  private readonly _blockchains = signal<BlockchainResponseAdminDto[]>([]);
  private readonly _options = signal<SelectOption<string>[]>([]);
  private readonly _labelById = new Map<string, string>();
  private _loaded = false;

  constructor(private readonly api: Api) {}

  /**
   * Load blockchains from backend (cached). Optionally pass a search string for server-side filtering.
   */
  async load(search?: string, force = false): Promise<void> {
    if (this._loaded && !force && !search) return; // simple cache; bypass when searching

    const list = await this.api.invoke(adminBlockchainsControllerListBlockchainsV1, {
      search,
    });

    if (!search) {
      // Only cache default (no search) result
      this._loaded = true;
      this._blockchains.set(list);
      this._options.set(list.map((b) => ({ label: b.label, value: b.id })));
      this._labelById.clear();
      for (const b of list) this._labelById.set(b.id, b.label);
    }
  }

  /** Returns cached blockchains array (load first if empty). */
  get blockchains() {
    return this._blockchains;
  }

  /** Options for PrimeNG Select: label + value=id */
  get options() {
    return this._options;
  }

  /** Lookup label by blockchain id; returns id if not found. */
  labelById(id?: string | null): string {
    if (!id) return '';
    return this._labelById.get(id) ?? id;
  }
}
